import delve from 'dlv';
import { ICountryItem, IEntities } from 'components/data-containers/KPIs/types';

/**
 * @param {Object} { data, entityIds, countryIds, compareMode, countryFilter }
 * @returns {Array} An array with flattened data, suitable for consumption by the TopCountry component
 * in the Territory KPIs. Not suited for any other data structures.
 */

interface IMapTerritoryDataProps {
  data: IEntities[];
  entityIds: string[];
  countryIds?: string[];
  compareMode?: string;
  countryFilter?: string;
  region?: string;
}

interface IShortNewItem {
  entityId: string;
}

interface INewItem {
  countryId: string;
  countryName: string;
  countryRank: number;
  entityId: string;
  llw: number;
  lw: number;
  lwtd: number;
  rtd: number;
  rtdOfGlobal: number;
  salesType: string;
  twtd: number;
  twtdOfGlobal: number;
  wowInProgress: number;
  wowLastCompleted: number;
}

const mapTerritoryData = ({
  data,
  entityIds,
  countryIds,
  compareMode,
  countryFilter,
  region,
}: IMapTerritoryDataProps): (INewItem | IShortNewItem)[] | null => {
  if (data) {
    /**
     * Create an empty object which we use to build up our return object
     */
    const result: (INewItem | IShortNewItem)[] = [];

    /**
     * For every entity we pass as an arguments (usually this is one entity,
     * but in entity compare these are 2 to 4 entityIds) we map the values to a new data object
     * It pushes the result of the forEach in a new array item in the result array.
     * e.g. result[0] = {}
     * This is all basically just to flatten the structure from the API
     */

    entityIds.forEach((entityId) => {
      const entity = data.find(
        (d: IEntities) => delve(d, 'entity.id') === entityId
      );

      // If there's no data, we should still return an object with the entityId, otherwise
      // we can not show the entities value as a DASH and its (optional: compare) color in the list
      if (entity?.data.length === 0) {
        result.push({
          entityId,
        });
      } else {
        // Otherwise filter the country data so that the top country only shows the proper country
        // data based on the filter and/or compare state.
        let countryData;
        if (compareMode === 'country') {
          // Filter the entity data so that it only returns countries that are being compared.
          countryData = entity?.data.filter((country: ICountryItem) =>
            countryIds?.includes(country.countryId)
          );
        } else if (
          countryFilter &&
          !Array.isArray(countryFilter) &&
          !(countryFilter && region) &&
          !countryFilter.includes('EX-')
        ) {
          // Filter the entity data so that it only returns the country that is set in the country filter
          countryData = entity?.data.filter(
            (country: ICountryItem) => country.countryId === countryFilter
          );
        } else {
          // Otherwise always get the first item of the list of 5.
          countryData = entity?.data.slice(0, 1);
        }

        // For every country that we want to display create a new object
        // which contains the entityId, top country, and all data for that top country
        countryData?.forEach((country: ICountryItem) => {
          const { countryId, countryName } = country;
          const newItem = {
            entityId,
            countryId,
            countryName,
            ...country.data[0], //this means we ignore the LIMIT parameters and always return only 1 top performing country per limit
          };
          result.push(newItem);
        });
      }
    });

    // Return the array of entities with the top performing country per entity
    return result;
  }
  return null;
};

export default mapTerritoryData;
